import { setPersistedOrgId } from "@appretio/auth";
import createClient, { Middleware } from "openapi-fetch";
import { createApiCallLogMiddleware } from "./diagnostics/apiCallLog";
import { ApiErrorCause } from "./errorHandling/parseApiError";
import {
  createJobPollingMiddleware,
  JOB_COMPLETED_HEADER
} from "./jobs/jobPolling";
import type { paths } from "./lib/api/schema";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Mirrors appretio.error.ErrorCode.UNKNOWN_TENANT: the token's tenant isn't
// served by this deployment (an org that exists in Auth0 but not in this
// environment's registry). Distinct from an ordinary 403, which must NOT log
// the user out of their tenant.
const UNKNOWN_TENANT_ERROR_CODE = 8;

type TokenGetter = () => Promise<string>;

// Coalesce concurrent token requests so parallel API calls share a single
// refresh-token grant. Without this, Auth0's rotating refresh tokens can be
// invalidated by racing refreshes (only the first wins; the rest get
// `invalid_grant`), leaving the session unable to renew.
function createSingleFlightGetter(getToken: TokenGetter): TokenGetter {
  let inFlight: Promise<string> | null = null;
  return () => {
    if (!inFlight) {
      inFlight = getToken().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
}

function createAuthMiddleware(getToken: TokenGetter): Middleware {
  return {
    async onRequest({ request }) {
      const accessToken = await getToken();
      request.headers.set("Authorization", `Bearer ${accessToken}`);
      return request;
    },
    async onResponse({ response }) {
      if (!response.ok) {
        let responseJson;
        try {
          responseJson = await response.clone().json();
        } catch {
          responseJson = null;
        }

        // The selected tenant can't be served here — every request under it
        // would 403 (including the org list feeding the tenant switcher, so
        // the user has no in-app way out). Drop the persisted org and restart
        // at the email screen; membership routing takes them somewhere valid.
        if (
          response.status === 403 &&
          responseJson?.error?.code === UNKNOWN_TENANT_ERROR_CODE
        ) {
          setPersistedOrgId(null);
          window.location.assign("/");
        }

        // The envelope's `error.message` carries the server's curated text
        // (guard rejections, validation summaries); the per-field validation
        // msg and the HTTP status text are fallbacks.
        const message =
          responseJson?.error?.message ??
          responseJson?.error?.data?.errors?.[0]?.msg ??
          response.statusText;

        // Numeric-seconds form only (what the API sends); the HTTP-date form
        // parses to NaN and is dropped, leaving the retry policy's backoff.
        const retryAfterSeconds = Number(response.headers.get("Retry-After"));

        const error = new Error(message, {
          cause: {
            status: response.status,
            jobCompleted: response.headers.get(JOB_COMPLETED_HEADER) === "true",
            statusMessage: message,
            ...(retryAfterSeconds > 0 && { retryAfterSeconds }),
            ...(responseJson?.error?.code != null && {
              code: responseJson.error.code
            }),
            ...(responseJson?.error?.data != null && {
              data: responseJson.error.data
            })
          } as ApiErrorCause
        });

        throw error;
      }
    }
  };
}

/**
 * Names the page a call serves; the API tags its Snowflake statements with it
 * to attribute cost per page. Contract: specs/architecture/api.md § Query tags.
 */
export function pageHeader(): Record<string, string> {
  return { "X-Appretio-Page": window.location.pathname.toLowerCase() };
}

function createPageMiddleware(): Middleware {
  return {
    onRequest({ request }) {
      for (const [name, value] of Object.entries(pageHeader())) {
        request.headers.set(name, value);
      }
      return request;
    }
  };
}

export function createApiClient(getToken: TokenGetter) {
  const baseClient = createClient<paths>({ baseUrl: API_BASE_URL });
  const sharedGetToken = createSingleFlightGetter(getToken);
  baseClient.use(createAuthMiddleware(sharedGetToken));
  baseClient.use(createPageMiddleware());
  // Registered after auth on purpose: onResponse runs in reverse order, so
  // the call log observes every response before the auth middleware turns
  // an error status into a throw. Feeds feedback's `context.diagnostics`.
  baseClient.use(createApiCallLogMiddleware());
  // Last, so its onResponse runs first: the call log and the auth middleware
  // see the job's final response, never the 202.
  baseClient.use(createJobPollingMiddleware(sharedGetToken));

  return baseClient;
}
